// Where the course comes from: a published release, or the bundled JSON.
//
// `load()` takes no language and returns a LoadedContent, rather than
// `load(lang)` plus a separate synchronous `version`. The old shape worked
// only because both halves came from the same synchronous import; a release
// resolved over the network cannot answer "which content is this" before it
// has been fetched, and Root needs the version to build the progress store
// before any course exists. Collapsing them into one awaitable value is what
// makes the ordering in Root.tsx expressible at all.

import { LANGS, type LangId } from "../lang.ts";
import { BundledAssetSource, type AssetSource } from "./assets.ts";
import type { Course, CourseData } from "./course.ts";
import { BUNDLED, LOCALES, makeCourse } from "./course.ts";
import type { Locale } from "./types.ts";
import { CONTENT_VERSION } from "./version.ts";

/** Not a release id — the bundled source has no release, and naming it one
    would let a snapshot be stamped as though a release had been verified. */
export const BUNDLED_RELEASE_ID = "bundled";

/**
 * Why a load failed, in the vocabulary the *port* uses rather than Appwrite's.
 *
 * This lives here, not in `appwrite.ts`, for two reasons. The Appwrite adapter
 * is the only implementation that can tell a 401 from a 404, but Root is the
 * only place that can act on the difference — an unauthorized load means the
 * remembered session died and the learner must sign in again, while an integrity
 * failure means retrying will not help and serving stale would be a lie. Giving
 * Root the distinction requires the class to live outside the adapter: Root
 * branches on `ContentError.kind`, and defining the class in `appwrite.ts`
 * would make that an import of the Appwrite adapter, coupling the whole app
 * to Appwrite for one branch.
 */
type ContentErrorKind = "offline" | "unavailable" | "unauthorized" | "forbidden" | "integrity";

export class ContentError extends Error {
	readonly kind: ContentErrorKind;
	constructor(kind: ContentErrorKind, message: string) {
		super(message);
		this.name = "ContentError";
		this.kind = kind;
	}
}

export interface LoadedContent {
	readonly releaseId: string;
	/**
	 * The narrow, language-neutral identity the progress fold is keyed on. Not
	 * the release id: a translation-only or audio-only release changes the
	 * release and must not invalidate a single snapshot.
	 */
	readonly contentVersion: string;
	course(lang: LangId): Course;
	readonly assets: AssetSource;
}

export interface ContentSource {
	load(): Promise<LoadedContent>;
}

/**
 * Build every language's course up front and hand them out by lookup.
 *
 * Eager, so a missing translation rejects the load — which Root renders as
 * LoadError with a retry — instead of throwing inside a later render. And a
 * Map, so `course("en")` hands back the SAME object for the same language,
 * pinned by `source.test.ts`. AppLayout hands that object to `CourseProvider`,
 * and it builds it in a `useMemo` keyed on `[content, st.lang]`, so the
 * identity is load-bearing: a fresh object per call would recompute the course
 * on every render. It cost a render loop once, when `loadCourse` built a
 * fresh Course per call.
 */
export function loadedContent(
	releaseId: string,
	contentVersion: string,
	data: CourseData,
	locales: Record<string, Locale>,
	assets: AssetSource,
): LoadedContent {
	const courses = new Map<LangId, Course>();
	for (const lang of LANGS) {
		const locale = locales[lang];
		// Missing locale for a language the build ships is a content bug, and
		// makeCourse's `t` would throw on the first missing key anyway. Catching it
		// here turns a render-time crash into a load-time failure.
		if (!locale) throw new Error(`Bundle has no ${lang} locale`);
		courses.set(lang, makeCourse(data, lang, locale));
	}
	return {
		releaseId,
		contentVersion,
		course: (lang) => {
			const c = courses.get(lang);
			if (!c) throw new Error(`No course for language: ${lang}`);
			return c;
		},
		assets,
	};
}

export class BundledContentSource implements ContentSource {
	async load(): Promise<LoadedContent> {
		return loadedContent(
			BUNDLED_RELEASE_ID,
			CONTENT_VERSION,
			BUNDLED,
			LOCALES,
			new BundledAssetSource(),
		);
	}
}
